import { TBlockType } from 't2-keystone/src/services/body/types';
import isArray from 'lodash/isArray';
import difference from 'lodash/difference';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import { getQuerySelectorForHighlightHint } from './getQuerySelectorForHighlightHint';
import { extractHtmlFromRange } from '../extractHtmlFromRange';
import { BlockType } from '../../../ArticleBodyRenderer/types';
import { HIGHLIGHT_SELECTION_HINT_ATTRIBUTE_NAME, HighlightSelectionHint } from '../../types';

const REQUIRED_GRANDPARENTS_MAP: {
  [key in BlockType]: HighlightSelectionHint[] | HighlightSelectionHint[][];
} = {
  [BlockType.Paragraph]: [HighlightSelectionHint.Text],
  [BlockType.Header]: [HighlightSelectionHint.Text],
  [BlockType.Code]: [HighlightSelectionHint.Code],
  [BlockType.Quote]: [[HighlightSelectionHint.Text], [HighlightSelectionHint.Caption]],
  [BlockType.Table]: [
    HighlightSelectionHint.Table,
    HighlightSelectionHint.TableRow,
    HighlightSelectionHint.TableColumn,
  ],
  [BlockType.List]: [HighlightSelectionHint.List, HighlightSelectionHint.ListElement],
  [BlockType.Image]: [[HighlightSelectionHint.Image], [HighlightSelectionHint.Caption]],
};

const wrapWithMissingParents = (range: Range, requiredParents: HighlightSelectionHint[]): string | false => {
  const html = extractHtmlFromRange(range);
  const { commonAncestorContainer } = range;

  const foundParentsReversed: { parent: HighlightSelectionHint[]; element: HTMLElement }[] = [];
  let current = commonAncestorContainer;

  requiredParents.toReversed().forEach((requiredParent) => {
    while (
      current &&
      (current.nodeType === Node.TEXT_NODE || !current?.matches(getQuerySelectorForHighlightHint(requiredParent)))
    ) {
      current = current?.parentElement;
    }
    if (current?.matches && current?.matches(getQuerySelectorForHighlightHint(requiredParent))) {
      foundParentsReversed.push({ element: current, parent: requiredParent });
      current = current?.parentElement;
    }
  });

  if (
    !isEqual(
      requiredParents,
      foundParentsReversed.toReversed().map(({ parent }) => parent),
    )
  ) {
    return false;
  }

  return foundParentsReversed.reduce((acc, { element, parent }) => {
    const tag = element.tagName.toLowerCase();
    return `<${tag} ${HIGHLIGHT_SELECTION_HINT_ATTRIBUTE_NAME}="${parent}">${acc}</${tag}>`;
  }, html);
};

const findPresentGrandparents = (
  range: Range,
  requiredGrandparents: HighlightSelectionHint[],
): HighlightSelectionHint[] => {
  const orderedAllMatchingGrandparents = range
    .cloneContents()
    .querySelectorAll(requiredGrandparents?.map(getQuerySelectorForHighlightHint).join(', '));
  const orderedAllMatchingGrandparentsType = Array.from(orderedAllMatchingGrandparents).map((element) =>
    element.getAttribute(HIGHLIGHT_SELECTION_HINT_ATTRIBUTE_NAME),
  );

  const presentGrandparents: HighlightSelectionHint[] = [];
  let checkIn = orderedAllMatchingGrandparentsType;
  requiredGrandparents?.toReversed().forEach((elementToCheck) => {
    const position = checkIn.indexOf(elementToCheck);
    if (position !== -1) {
      presentGrandparents.push(elementToCheck);
      checkIn = checkIn.slice(0, position);
    }
  });

  return presentGrandparents.toReversed();
};

const wrapSelectionHtmlWithRequiredGrandparentsByRequiredGrandparents = (
  range: Range,
  requiredGrandparents: HighlightSelectionHint[] | HighlightSelectionHint[][],
): string | false => {
  if (isArray(requiredGrandparents?.[0])) {
    return requiredGrandparents.reduce((acc: false | string, el: HighlightSelectionHint[]) => {
      if (acc) {
        return acc;
      }

      return wrapSelectionHtmlWithRequiredGrandparentsByRequiredGrandparents(range, el);
    }, false);
  }

  const presentGrandparents = findPresentGrandparents(range, requiredGrandparents);
  const missingGrandparents = difference(requiredGrandparents, presentGrandparents);

  if (isEmpty(missingGrandparents)) {
    return extractHtmlFromRange(range);
  }

  return wrapWithMissingParents(range, missingGrandparents);
};

export const wrapSelectionHtmlWithRequiredGrandparents = (range: Range, blockType: TBlockType): string => {
  const requiredGrandparents = REQUIRED_GRANDPARENTS_MAP[blockType];

  const result = wrapSelectionHtmlWithRequiredGrandparentsByRequiredGrandparents(range, requiredGrandparents);
  if (result === false) {
    return extractHtmlFromRange(range);
  }

  return result;
};
